import {
  BulkActionRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { translation } from "@/i18n";
import { nsView } from "@/i18n/locales/view";
import React, { useEffect } from "react";
import { View, useSetView } from "../../../../../domain/View";
import {
  useGetViewConfigSchemas,
  useSetViewConfigSchema,
} from "../../../../../domain/ViewConfigSchema";
import {
  useGetViewDependSheets,
  useSetViewDependSheet,
} from "../../../../../domain/ViewDependSheet";
import {
  useGetViewDependSheetFields,
  useSetViewDependSheetField,
} from "../../../../../domain/ViewDependSheetField";
import {
  duplicateRecursiveViewData,
  getNewDuplicationViewTitleWithNumbering,
} from "../helper";

const useTranslation = translation.use(nsView);

/**
 * 1件のビューを即時複製し、Toast で進捗・結果を通知するフック。
 * BulkActionRequest.phase が "requested" かつ itemIds.length === 1 のときに実行する。
 */
export const useViewDuplicateAction = ({
  views,
  duplicateRequest,
  setDuplicateRequest,
  setSelectedViewIds,
}: {
  views: View[];
  duplicateRequest: BulkActionRequest;
  setDuplicateRequest: React.Dispatch<React.SetStateAction<BulkActionRequest>>;
  setSelectedViewIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const { t } = useTranslation();
  const setView = useSetView();
  const setViewConfigSchema = useSetViewConfigSchema();
  const setViewDependSheet = useSetViewDependSheet();
  const setViewDependSheetField = useSetViewDependSheetField();
  const getViewConfigSchemas = useGetViewConfigSchemas();
  const getViewDependSheetFields = useGetViewDependSheetFields();
  const getViewDependSheets = useGetViewDependSheets();

  const { runWithToast } = useBulkActionToast({
    inProgress: () => t.view.duplicate.inProgress(),
    success: () => t.view.duplicate.success(),
    error: () => t.view.duplicate.error(),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      duplicateRequest.phase !== "requested" ||
      duplicateRequest.itemIds.length !== 1
    )
      return;

    const duplicateView = views.find(
      (v) => v.id === duplicateRequest.itemIds[0]
    );
    if (!duplicateView) {
      // 対象ビューが存在しない場合（他ユーザーによる削除など）は idle に戻して操作を回復する
      setDuplicateRequest({ phase: "idle", itemIds: [] });
      return;
    }

    // 即時 in_progress へ遷移してリマウント時の二重実行を防ぐ
    setDuplicateRequest({
      phase: "in_progress",
      itemIds: duplicateRequest.itemIds,
    });

    const run = async () => {
      const { allSuccess } = await runWithToast([duplicateView], async () => {
        const newTitle = getNewDuplicationViewTitleWithNumbering(
          views,
          duplicateView.title
        );
        await duplicateRecursiveViewData({
          duplicateView,
          duplicateViewTitle: newTitle,
          getViewDependSheets,
          getViewConfigSchemas,
          getViewDependSheetFields,
          setView,
          setViewConfigSchema,
          setViewDependSheet,
          setViewDependSheetField,
          sort: views.length,
        });
      });
      if (allSuccess) setSelectedViewIds([]);
      setDuplicateRequest({ phase: "idle", itemIds: [] });
    };

    run();
  }, [duplicateRequest.phase]);
};
